import { Box, Grid, Paper, TextField, MenuItem, Button } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // make sure to install @mui/x-date-pickers
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import dayjs from "dayjs";
import { PumpTransactionRequest } from "./PumpTransactionRequest";
import { PumpTransactionResponse } from "./PumpTransactionResponse";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function PumpTransaction() {
  // 🔹 Table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // 🔹 Map frontend field names → backend DB names
  const orderByMap = {
    transaction: "transaction",
    dateTimeStart: "date_time_start",
    dateTime: "date_time",
    pump: "pump",
    nozzle: "nozzle",
    price: "price",
    volume: "volume",
    amount: "amount",
    userId: "user_id",
  };

  // 🔹 Dropdown options (from API)
  const [pumps, setPumps] = useState([]);
  const [users, setUsers] = useState([]);
  const [fuels, setFuels] = useState([]);

  // 🔹 Search filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pump, setPump] = useState("");
  const [nozzle, setNozzle] = useState("");
  const [fuelId, setFuelId] = useState("");
  const [userId, setUserId] = useState("");

  useEffect(() => {
    axiosClient
      .get("/pump-transaction/get-all-pts-user-list") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device types: " + JSON.stringify(data));
        setUsers(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));

    axiosClient
      .get("/pump-transaction/get-all-fuel-grade-list") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device state: " + JSON.stringify(data));
        setFuels(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));

    axiosClient
      .get("/pump-transaction/get-all-pump-list") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device state: " + JSON.stringify(data));
        setPumps(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // 🔹 Fetch Data
  const fetchData = () => {
    const token = localStorage.getItem("token");
    console.log("token: " + token);
    setLoading(true);

    const request = new PumpTransactionRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0]; // { field: "codeDescription", sort: "asc" }
      const backendField = orderByMap[sort.field] || sort.field;
      console.log(`order by ${backendField} ${sort.sort?.toUpperCase()}`);
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`
      );
    }

    if (startDate) request.setStartDate(startDate.toISOString());
    if (endDate) request.setEndDate(endDate.toISOString());
    if (pump) request.setPump(Number(pump));
    if (nozzle) request.setNozzle(Number(nozzle));
    if (fuelId) request.setFuelId(Number(fuelId));
    if (userId) request.setUserId(Number(userId));

    axiosClient
      .post("/pump-transaction/get-all-pump-transaction", request)
      .then((res) => {
        const pumpTransactionResponse = new PumpTransactionResponse(res.data);
        if (
          pumpTransactionResponse?.getOperationResult() &&
          pumpTransactionResponse.getPumpTransactionList()
        ) {
          setRows(pumpTransactionResponse.getPumpTransactionList());
          setRowCount(pumpTransactionResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  // initial load + refetch when page/pageSize changes
  useEffect(() => {
    console.log(page, pageSize);
    fetchData();
  }, [page, pageSize, sortModel]);

  // 🔹 Columns
  const columns = [
    { field: "transaction", headerName: "Transaction", width: 120 },
    {
      field: "dateTimeStart",
      headerName: "Date Time Start",
      width: 170,
      renderCell: (params) => {
        const raw = params.row?.dateTimeStart;
        return raw ? dayjs(raw).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    {
      field: "dateTime",
      headerName: "Date Time End",
      width: 170,
      renderCell: (params) => {
        const raw = params.row?.dateTime;
        return raw ? dayjs(raw).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    { field: "pump", headerName: "Pump", flex: 1 },
    {
      field: "nozzle",
      headerName: "Nozzle (Fuel)",
      flex: 1,
      renderCell: (params) => {
        const nozzle = params.row?.nozzle;
        const fuel = params.row?.fuelGradeName;
        return nozzle && fuel ? `${nozzle} (${fuel})` : "";
      },
    },
    { field: "price", headerName: "Price (TK)", flex: 1 },
    { field: "volume", headerName: "Volume (L)", flex: 1 },
    { field: "amount", headerName: "Amount (TK)", flex: 1 },
    {
      field: "userId",
      headerName: "Employee",
      flex: 1,
      renderCell: (params) => {
        const raw = params.row?.userId;
        const user = raw ? users.find((u) => u.id === raw) : null;
        return user ? user.name : "";
      },
    },
  ];

  return (
    <Layout title="Pump Transaction">
      {/* Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          {/* search fields ... */}
          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="Start Date"
              value={startDate}
              onChange={(newVal) => setStartDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="End Date"
              value={endDate}
              onChange={(newVal) => setEndDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Pump"
              value={pump}
              onChange={(e) => setPump(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {pumps.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Nozzle"
              value={nozzle}
              onChange={(e) => setNozzle(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {[1, 2, 3, 4, 5].map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Fuel"
              value={fuelId}
              onChange={(e) => setFuelId(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {fuels.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Employee"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {users.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.transaction}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
          sx={{ minWidth: { xs: 1120, sm: "100%" }, bgcolor: "white" }}
        />
      </Box>
    </Layout>
  );
}
